import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import useSEO from '../hooks/useSEO';
import { formatUsdPrice } from '../utils/gamePrice';

function formatMoney(amount) {
  if (amount == null || Number.isNaN(amount)) {
    return '$0.00';
  }
  return formatUsdPrice(Number(amount));
}

function formatMonthLabel(monthKey) {
  if (!monthKey) {
    return '';
  }
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function formatDateTime(value, compact = false) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (compact) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DashboardShell({ children, onRefresh, refreshing, onBack }) {
  return (
    <div className="min-h-screen bg-dark-bg overflow-x-hidden">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(37,99,235,0.08)_0%,_transparent_55%)]" aria-hidden="true" />

      <header className="fixed top-2 sm:top-4 left-0 right-0 z-50 border border-dark-border rounded-2xl sm:rounded-3xl mx-2 md:mx-5 px-3 sm:px-4 md:px-8 py-2.5 sm:py-3 md:py-4 shadow-2xl bg-gradient-to-br from-dark-surface via-dark-card to-dark-border backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0">
            <img src="/images/logo_white.png" alt="Nightfall" className="h-6 sm:h-7 md:h-8 w-auto flex-shrink-0" />
            <span className="font-normal text-sm sm:text-xl md:text-2xl text-white tracking-tight sm:border-l-2 sm:border-white sm:pl-3 md:pl-4 truncate">
              Creator Hub
            </span>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200 disabled:opacity-50"
                aria-label="Refresh dashboard"
              >
                <svg
                  className={`w-5 h-5 md:w-6 md:h-6 text-white ${refreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onBack}
              className="p-2 md:p-2.5 bg-dark-card/60 backdrop-blur-sm border border-dark-border rounded-full hover:bg-dark-card hover:border-accent-blue/30 transition-all duration-200"
              aria-label="Exit to store"
            >
              <svg
                className="w-5 h-5 md:w-6 md:h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <main className="relative pt-[4.5rem] sm:pt-24 md:pt-32 px-3 sm:px-4 md:px-5 pb-8 sm:pb-12 w-full max-w-full">
        {children}
      </main>
    </div>
  );
}

function PanelCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-xl ${className}`}>
      {(title || subtitle) && (
        <div className="mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-dark-border flex flex-wrap items-end justify-between gap-2">
          {title && <h2 className="font-semibold text-lg sm:text-xl lg:text-2xl text-white">{title}</h2>}
          {subtitle && <span className="text-sm text-white/50">{subtitle}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function creatorEarningsFromGross(grossUsd, feePercent) {
  const share = (100 - feePercent) / 100;
  return Math.round(Number(grossUsd || 0) * share * 100) / 100;
}

function StatCard({ label, value, subtext, variant = 'secondary', className = '' }) {
  const isHero = variant === 'hero';

  return (
    <div
      className={`transition-all duration-200 ${
        isHero
          ? 'rounded-2xl sm:rounded-3xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface p-5 sm:p-6 lg:p-8 shadow-xl hover:border-white/20'
          : 'rounded-xl sm:rounded-2xl border border-dark-border/90 bg-gradient-to-br from-dark-card/90 to-dark-surface/80 p-3.5 sm:p-4 lg:p-5 shadow-md hover:border-white/15'
      } ${className}`}
    >
      <p
        className={`font-medium uppercase tracking-wider text-white/45 ${
          isHero ? 'text-xs mb-3' : 'text-[11px] mb-1.5'
        }`}
      >
        {label}
      </p>
      <p
        className={`font-semibold text-white tracking-tight tabular-nums ${
          isHero ? 'text-3xl sm:text-4xl lg:text-5xl' : 'text-lg sm:text-xl lg:text-2xl text-white/90'
        }`}
      >
        {value}
      </p>
      {subtext && (
        <p className={`mt-2 ${isHero ? 'text-sm text-white/50' : 'text-xs text-white/40'}`}>{subtext}</p>
      )}
    </div>
  );
}

function PayoutsPanel({ payouts, token, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  if (!payouts) {
    return null;
  }

  const statusCopy = {
    active: {
      title: 'Payouts active',
      body: 'Card sales for your games are paid out to your Stripe account automatically.',
    },
    pending: {
      title: 'Finish payout setup',
      body: 'Stripe still needs a few details before money can be sent to your account.',
    },
    not_started: {
      title: 'Set up payouts',
      body: 'Connect your payout details through Stripe so you get paid when someone buys your games.',
    },
  };

  const copy = statusCopy[payouts.status] || statusCopy.not_started;

  const startOnboarding = async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    setActionError(null);

    try {
      const response = await fetch(`${API_URL}/creator/payouts/onboarding-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok || !data.url) {
        const message = data.error || 'Could not start payout setup';
        setActionError(
          data.helpUrl
            ? `${message} Open: ${data.helpUrl}`
            : message
        );
        return;
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Payout onboarding error:', err);
      setActionError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    setActionError(null);

    try {
      const response = await fetch(`${API_URL}/creator/payouts/dashboard-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!response.ok || !data.url) {
        setActionError(data.error || 'Could not open Stripe dashboard');
        return;
      }

      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Stripe dashboard link error:', err);
      setActionError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PanelCard title="Payouts">
      <div className="rounded-xl sm:rounded-2xl border border-dark-border bg-dark-bg/40 p-4 sm:p-5 lg:p-6">
        <p className="text-base sm:text-lg font-semibold text-white">{copy.title}</p>
        <p className="text-sm text-white/50 mt-2 leading-relaxed">{copy.body}</p>

        {actionError && (
          <p className="text-sm text-red-400 mt-4">{actionError}</p>
        )}

        <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-3">
          {payouts.status !== 'active' ? (
            <button
              type="button"
              onClick={startOnboarding}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-3 rounded-xl bg-accent-blue/15 border border-accent-blue/35 text-accent-blue text-sm font-semibold hover:bg-accent-blue/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? 'Opening Stripe…' : payouts.status === 'pending' ? 'Continue setup' : 'Set up with Stripe'}
            </button>
          ) : (
            <button
              type="button"
              onClick={openStripeDashboard}
              disabled={loading}
              className="w-full sm:w-auto px-5 py-3 rounded-xl border border-dark-border bg-dark-bg/50 text-white/80 text-sm font-semibold hover:text-white hover:border-white/20 transition-all duration-200 disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Manage payout account'}
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="w-full sm:w-auto px-5 py-3 rounded-xl border border-dark-border text-white/60 text-sm font-semibold hover:text-white transition-all duration-200 disabled:opacity-50"
          >
            Refresh status
          </button>
        </div>

        <p className="text-xs text-white/40 mt-4 leading-relaxed">
          Bank and tax details are collected securely by Stripe. Nightfall does not store your payout credentials.
        </p>
      </div>
    </PanelCard>
  );
}

function StatsGrid({ summary }) {
  const last30Earnings = creatorEarningsFromGross(
    summary.revenueLast30Days,
    summary.platformFeePercent
  );

  const secondaryStats = [
    {
      label: 'Last 30 days',
      value: formatMoney(last30Earnings),
      subtext: `${summary.salesLast30Days} sales`,
    },
    {
      label: 'Downloads',
      value: summary.totalDownloads.toLocaleString(),
      subtext: `${summary.downloadsLast30Days} last 30 days`,
    },
    {
      label: 'Likes',
      value: summary.totalLikes.toLocaleString(),
      subtext: 'Catalog',
    },
    {
      label: 'Wishlist',
      value: summary.totalWishlist.toLocaleString(),
      subtext: 'Saved',
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2.5 sm:gap-3 lg:gap-4 items-stretch">
      <StatCard
        variant="hero"
        className="lg:col-span-5 min-h-0 sm:min-h-[120px] lg:min-h-[168px] flex flex-col justify-center"
        label="Total earnings"
        value={formatMoney(summary.estimatedEarningsUsd)}
        subtext={`${summary.totalSales} ${summary.totalSales === 1 ? 'sale' : 'sales'}`}
      />
      <div className="lg:col-span-7 grid grid-cols-2 gap-2.5 sm:gap-3 lg:gap-4">
        {secondaryStats.map((stat) => (
          <StatCard
            key={stat.label}
            variant="secondary"
            label={stat.label}
            value={stat.value}
            subtext={stat.subtext}
          />
        ))}
      </div>
    </div>
  );
}

const dashboardPanelListShellClass =
  'flex-1 min-h-[200px] sm:min-h-[240px] lg:min-h-[320px] rounded-xl sm:rounded-2xl border border-dark-border bg-dark-bg/40 overflow-hidden flex flex-col';

const dashboardPanelEmptyClass =
  'flex-1 min-h-[200px] sm:min-h-[240px] lg:min-h-[320px] flex items-center justify-center rounded-xl sm:rounded-2xl border border-dark-border bg-dark-bg/40 text-white/50 text-sm px-4 text-center';

function DashboardPanelListRow({ primary, secondary, value, href, children }) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-white min-w-0 flex-1 leading-snug line-clamp-2">{primary}</p>
        <p className="text-sm sm:text-base font-semibold text-white tabular-nums flex-shrink-0">{value}</p>
      </div>
      {secondary && <p className="text-xs text-white/45 mt-1.5">{secondary}</p>}
      {children}
    </>
  );

  const rowClass = 'block px-3 py-3 sm:px-4 sm:py-4 hover:bg-dark-bg/50 transition-colors';

  if (href) {
    return (
      <Link to={href} className={rowClass}>
        {inner}
      </Link>
    );
  }

  return <div className={rowClass}>{inner}</div>;
}

function EarningsByMonthList({ salesByMonth, maxMonthRevenue, platformFeePercent }) {
  return (
    <div className={dashboardPanelListShellClass}>
      <ul className="flex-1 overflow-y-auto divide-y divide-dark-border">
        {salesByMonth.map((row) => {
          const monthEarnings = creatorEarningsFromGross(row.revenueUsd, platformFeePercent);
          const barWidth = Math.max(6, (row.revenueUsd / maxMonthRevenue) * 100);

          return (
            <li key={row.month}>
              <DashboardPanelListRow
                primary={formatMonthLabel(row.month)}
                value={formatMoney(monthEarnings)}
                secondary={`${row.salesCount} ${row.salesCount === 1 ? 'sale' : 'sales'}`}
              >
                <div className="mt-3 h-1.5 rounded-full bg-dark-surface border border-dark-border overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent-blue/80 to-accent-cyan/70"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </DashboardPanelListRow>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RecentSalesList({ recentSales }) {
  return (
    <div className={dashboardPanelListShellClass}>
      <ul className="flex-1 overflow-y-auto divide-y divide-dark-border">
        {recentSales.map((sale, index) => (
          <li key={`${sale.gameId}-${sale.purchasedAt}-${index}`}>
            <DashboardPanelListRow
              href={`/game/${sale.gameId}`}
              primary={sale.gameTitle}
              value={sale.paidWithShards ? `${sale.shardAmount} shards` : formatMoney(sale.amountUsd)}
              secondary={
                <>
                  <span className="sm:hidden">{formatDateTime(sale.purchasedAt, true)}</span>
                  <span className="hidden sm:inline">{formatDateTime(sale.purchasedAt)}</span>
                </>
              }
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function GamePerformanceCard({ game, gameEarnings }) {
  const metrics = [
    ['Sales', game.salesCount],
    ['30 days', game.salesLast30Days],
    ['Downloads', game.downloads],
    ['Likes', game.likes],
    ['Wishlist', game.wishlist],
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 rounded-2xl sm:rounded-3xl border border-dark-border bg-gradient-to-br from-dark-card to-dark-surface shadow-xl overflow-hidden hover:border-accent-blue/25 transition-all duration-200">
      <Link
        to={`/game/${game.id}`}
        className="relative block w-full aspect-[16/9] max-h-[220px] sm:max-h-none sm:aspect-auto sm:h-full overflow-hidden bg-dark-card"
      >
        {game.mainImage ? (
          <img
            src={game.mainImage}
            alt={game.title}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-dark-border via-dark-card to-dark-surface" />
        )}
      </Link>
      <div className="min-w-0 flex flex-col border-t sm:border-t-0 sm:border-l border-dark-border bg-gradient-to-b from-dark-surface/60 to-dark-card/80">
        <div className="flex flex-1 flex-col px-4 py-4 sm:px-5 sm:py-5 lg:px-7 lg:py-6">
          <div>
            <Link
              to={`/game/${game.id}`}
              className="text-lg sm:text-xl lg:text-2xl font-semibold text-white leading-tight hover:opacity-80 transition-opacity line-clamp-2"
            >
              {game.title}
            </Link>
            <p className="text-xs sm:text-sm lg:text-base text-white/55 mt-1.5 sm:mt-2">
              <span className="text-white/45">Current price: </span>
              <span className="text-white/80 font-medium">
                {game.costUSD != null ? formatMoney(game.costUSD) : `${game.shards} shards`}
              </span>
              {game.featured && (
                <span className="ml-2 inline-flex items-center rounded-md bg-accent-blue/15 border border-accent-blue/25 px-2 py-0.5 text-xs font-medium text-accent-blue">
                  Featured
                </span>
              )}
            </p>
          </div>

          <div className="my-4 sm:my-5 lg:my-6 py-3 sm:py-4 lg:py-5 border-y border-dark-border/90">
            <p className="text-xs font-medium uppercase tracking-wider text-white/45">Earnings</p>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-1.5 sm:mt-2 tabular-nums tracking-tight">
              {formatMoney(gameEarnings)}
            </p>
          </div>

          <div className="mt-auto">
            <p className="text-xs font-medium uppercase tracking-wider text-white/45 mb-2 sm:mb-3">Performance</p>
            <div className="grid grid-cols-2 sm:flex sm:overflow-hidden rounded-xl sm:rounded-2xl border border-dark-border bg-dark-bg/40 gap-2 sm:gap-0 p-2 sm:p-0">
              {metrics.map(([label, value], index) => (
                <div
                  key={label}
                  className={`min-w-0 py-2 sm:py-3 lg:py-4 px-1 text-center rounded-lg sm:rounded-none bg-dark-card/40 sm:bg-transparent ${
                    index > 0 ? 'sm:border-l border-dark-border' : ''
                  } ${index === 4 ? 'col-span-2 sm:col-span-1' : ''}`}
                >
                  <p className="text-[9px] sm:text-[10px] lg:text-xs font-medium uppercase tracking-wider text-white/45 leading-tight">
                    {label}
                  </p>
                  <p className="text-sm sm:text-base lg:text-xl font-semibold text-white mt-1 sm:mt-1.5 tabular-nums">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatorDashboard() {
  useSEO({
    title: 'Creator Dashboard | Nightfall',
    description: 'View sales, downloads, and performance for your games on Nightfall.',
    url: 'https://nightfallshop.com/creator/dashboard',
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, token, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSupport, setShowSupport] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [supportSuccess, setSupportSuccess] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/creator/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 403) {
        setError('access_denied');
        setData(null);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load dashboard');
      }

      const json = await response.json();
      setData(json);
    } catch (err) {
      console.error('Creator dashboard error:', err);
      setError('load_failed');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    if (user?.role !== 'creator') {
      setError('access_denied');
      setLoading(false);
      return;
    }
    fetchDashboard();
  }, [authLoading, isAuthenticated, user?.role, navigate, fetchDashboard]);

  useEffect(() => {
    const connect = searchParams.get('connect');
    if (connect !== 'return' && connect !== 'refresh') {
      return;
    }
    if (user?.role === 'creator' && token) {
      fetchDashboard();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, fetchDashboard, user?.role, token]);

  const handleSupportSubmit = async () => {
    if (!supportMessage.trim() || !token) {
      return;
    }

    try {
      setSupportSending(true);
      setSupportError(null);
      const body = `[Creator Dashboard] ${supportMessage.trim()}`;
      const response = await fetch(`${API_URL}/auth/bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: body.slice(0, 2000) }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to send message');
      }

      setSupportSuccess(true);
      setSupportMessage('');
    } catch (err) {
      setSupportError(err.message || 'Failed to send message');
    } finally {
      setSupportSending(false);
    }
  };

  const goToStore = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <DashboardShell onBack={goToStore}>
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-12 h-12 rounded-full border-2 border-accent-blue/30 border-t-accent-blue animate-spin mb-4" />
          <p className="text-white/60 font-medium">Loading your dashboard...</p>
        </div>
      </DashboardShell>
    );
  }

  if (error === 'access_denied') {
    return (
      <DashboardShell onBack={goToStore}>
        <PanelCard className="max-w-lg mx-auto mt-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-accent-blue/10 border border-accent-blue/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-accent-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">Creator access only</h1>
          <p className="text-white/60 mb-8">
            This hub is for linked creator accounts. Contact Nightfall support if you need access.
          </p>
          <button
            type="button"
            onClick={goToStore}
            className="px-8 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
          >
            Back to store
          </button>
        </PanelCard>
      </DashboardShell>
    );
  }

  if (error || !data) {
    return (
      <DashboardShell onBack={goToStore} onRefresh={fetchDashboard} refreshing={loading}>
        <PanelCard className="max-w-lg mx-auto mt-8 text-center">
          <h1 className="text-2xl font-semibold text-white mb-3">Could not load dashboard</h1>
          <p className="text-white/60 mb-8">Please try again in a moment.</p>
          <button
            type="button"
            onClick={fetchDashboard}
            className="px-8 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 transition-all duration-200"
          >
            Retry
          </button>
        </PanelCard>
      </DashboardShell>
    );
  }

  const { creator, summary, games, recentSales, salesByMonth, payouts } = data;
  const maxMonthRevenue = salesByMonth.length > 0
    ? Math.max(...salesByMonth.map((r) => r.revenueUsd), 1)
    : 1;

  return (
    <DashboardShell onBack={goToStore} onRefresh={fetchDashboard} refreshing={loading}>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 items-start w-full">
        <div className="w-full lg:w-[80%] lg:flex-1 min-w-0 flex flex-col gap-4 sm:gap-6 lg:gap-8 order-1 lg:order-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-white/40 mb-1 sm:mb-2">Overview</p>
            <h1 className="font-semibold text-2xl sm:text-3xl lg:text-4xl text-white">Creator Hub</h1>
          </div>

          <StatsGrid summary={summary} />

          <PayoutsPanel payouts={payouts} token={token} onRefresh={fetchDashboard} />

          <div>
            <div className="flex flex-wrap items-end justify-between gap-2 mb-3 sm:mb-4">
              <h2 className="font-semibold text-xl sm:text-2xl text-white">Your games</h2>
              <span className="text-xs sm:text-sm text-white/50">Sorted by earnings</span>
            </div>
            {games.length === 0 ? (
              <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl p-8 sm:p-10 text-center">
                <p className="text-white/50 text-sm sm:text-base">No games linked to your creator profile yet.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:gap-4 lg:gap-5">
                {games.map((game) => (
                  <GamePerformanceCard
                    key={game.id}
                    game={game}
                    gameEarnings={creatorEarningsFromGross(
                      game.revenueUsd,
                      summary.platformFeePercent
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          <div
            className={`grid gap-4 sm:gap-6 lg:gap-8 items-stretch ${
              salesByMonth.length > 0 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
            }`}
          >
            {salesByMonth.length > 0 && (
              <PanelCard title="Earnings by month" className="min-w-0 flex flex-col h-full">
                <div className="flex-1 flex flex-col min-h-0">
                  <EarningsByMonthList
                    salesByMonth={salesByMonth}
                    maxMonthRevenue={maxMonthRevenue}
                    platformFeePercent={summary.platformFeePercent}
                  />
                </div>
              </PanelCard>
            )}

            <PanelCard title="Recent sales" className="min-w-0 flex flex-col h-full">
              <div className="flex-1 flex flex-col min-h-0">
                {recentSales.length === 0 ? (
                  <div className={dashboardPanelEmptyClass}>No sales recorded yet.</div>
                ) : (
                  <RecentSalesList recentSales={recentSales} />
                )}
              </div>
            </PanelCard>
          </div>
        </div>

        <aside className="w-full lg:w-[20%] lg:min-w-[240px] flex-shrink-0 flex flex-col gap-4 sm:gap-6 order-2 lg:order-1 lg:sticky lg:top-28">
          <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl">
            <div className="flex flex-row lg:flex-col gap-4 lg:gap-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-full lg:h-auto flex-shrink-0 rounded-xl sm:rounded-2xl overflow-hidden border border-dark-border bg-gradient-to-br from-dark-border via-dark-card to-dark-surface lg:aspect-square lg:mb-5">
                {creator.pfp ? (
                  <img src={creator.pfp} alt={creator.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-accent-blue/20 border-2 border-accent-blue/40 flex items-center justify-center">
                      <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-accent-blue/60">{creator.name.charAt(0)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 flex flex-col justify-center lg:justify-start">
                <h2 className="font-semibold text-lg sm:text-xl lg:text-2xl text-white mb-0.5 sm:mb-1 leading-tight truncate">{creator.name}</h2>
                {user?.username && (
                  <p className="text-white/50 text-xs sm:text-sm mb-1 sm:mb-2 lg:mb-3 truncate">@{user.username}</p>
                )}
                <p className="text-white/60 text-xs sm:text-sm lg:mb-5 lg:pb-5 lg:border-b lg:border-dark-border">
                  {summary.gamesCount} {summary.gamesCount === 1 ? 'game' : 'games'} on Nightfall
                </p>
              </div>
            </div>
            {creator.id && (
              <Link
                to={`/creator/${creator.id}`}
                className="flex w-full items-center justify-center gap-2 px-4 py-2.5 sm:py-3 mt-4 rounded-xl border border-dark-border bg-dark-bg/50 text-white/80 text-sm font-semibold hover:text-white hover:border-accent-blue/40 transition-all duration-200"
              >
                Public page
                <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </Link>
            )}
          </div>

          <PanelCard title="Creator tools">
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setShowSupport(true)}
                className="w-full px-4 py-3.5 rounded-xl border border-yellow-600/30 bg-yellow-600/10 text-yellow-400 text-sm font-semibold hover:bg-yellow-600/20 transition-all duration-200"
              >
                Creator support
              </button>
            </div>
          </PanelCard>
        </aside>
      </div>

      {showSupport && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-3 sm:p-5"
          onClick={() => {
            if (!supportSending) {
              setShowSupport(false);
              setSupportError(null);
              setSupportSuccess(false);
            }
          }}
        >
          <div
            className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-2xl sm:rounded-3xl overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl sm:my-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-8">
              <div className="mb-6 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 text-center">Creator support</h2>
              <p className="text-white/60 text-sm text-center mb-6">
                Questions about payouts, listings, or analytics? Our team will follow up by email.
              </p>
              {supportError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm">
                  {supportError}
                </div>
              )}
              {supportSuccess && (
                <div className="mb-4 bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-green-400 text-sm">
                  Message sent. We will get back to you soon.
                </div>
              )}
              <textarea
                value={supportMessage}
                onChange={(e) => {
                  setSupportMessage(e.target.value);
                  setSupportError(null);
                }}
                placeholder="Describe what you need help with..."
                className="w-full h-32 px-4 py-3 bg-dark-bg/50 border border-dark-border rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all resize-none mb-4"
                disabled={supportSending || supportSuccess}
                maxLength={1900}
              />
              <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowSupport(false);
                    setSupportSuccess(false);
                  }}
                  disabled={supportSending}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold bg-dark-surface/50 border border-dark-border text-white hover:bg-dark-surface/70 disabled:opacity-50 transition-all duration-200"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleSupportSubmit}
                  disabled={supportSending || supportSuccess || !supportMessage.trim()}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {supportSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

export default CreatorDashboard;
